// Frame geometry shared by the 16:9 Slide Designer and the PPTX exporter.
// A frame is { x, y, w, h } in percent of the slide's CONTENT REGION — the
// area below the section header bar. The exported slide is 13.333333333in x 7.5in
// with a 0.85in header, so the content region is 13.333333333in x 6.65in; the
// designer stage uses that exact aspect ratio, making layout map 1:1 onto
// exported slide coordinates.

export const EXPORT_SLIDE_W = 960 / 72;
export const EXPORT_SLIDE_H = 7.5;
export const EXPORT_HEADER_H = 0.85;
export const EXPORT_CONTENT_H = EXPORT_SLIDE_H - EXPORT_HEADER_H;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Cascade layout for blocks that have never been positioned
export const frameForIndex = (index = 0) => ({
    x: 6 + (index % 3) * 4,
    y: 5 + (index % 4) * 14,
    w: 46,
    h: 28
});

/** The whole content region: the slide minus its header bar. */
export const FULL_BLEED_FRAME = { x: 0, y: 0, w: 100, h: 100 };

/**
 * The frame a block starts in.
 *
 * A Custom HTML/CSS block is the exception to the cascade. It is not an object
 * placed on a slide — it *is* the slide's body, written to the content
 * region's proportions, and the box it is given is the box it is laid out in:
 * `height: 100%`, a percentage and `1cqw` all mean the frame. Handed the
 * cascade's 46% x 28% card, a layout designed for the slide was re-flowed into
 * a quarter of it and clipped, and the author's first job was always to drag
 * the handles back out to the edges. So it starts at the edges.
 *
 * Only ever a default: a block that has been positioned keeps its own frame.
 */
export const frameForBlock = (block, index = 0) => {
    if (block?.frame) return normalizeFrame(block.frame, index);
    if (block?.type === 'custom-html') return normalizeFrame(FULL_BLEED_FRAME, index);
    return normalizeFrame(null, index);
};

// Text scale bounds. `s` is a font multiplier applied to a block's text so
// the box can grow/shrink the text PowerPoint-style; only stored when it
// actually differs from 1 so untouched frames stay clean.
export const MIN_TEXT_SCALE = 0.3;
export const MAX_TEXT_SCALE = 4;

export const normalizeFrame = (frame, fallbackIndex = 0) => {
    const base = frame && typeof frame === 'object' ? frame : frameForIndex(fallbackIndex);
    const w = clamp(Number(base.w) || 46, 4, 100);
    const h = clamp(Number(base.h) || 28, 4, 100);
    const normalized = {
        x: clamp(Number(base.x) || 0, 0, 100 - Math.min(w, 8)),
        y: clamp(Number(base.y) || 0, 0, 100 - Math.min(h, 6)),
        w,
        h
    };
    const s = Number(base.s);
    if (Number.isFinite(s) && Math.abs(s - 1) > 0.001) {
        normalized.s = clamp(s, MIN_TEXT_SCALE, MAX_TEXT_SCALE);
    }
    return normalized;
};

/* --- Groups ------------------------------------------------------------- */

/**
 * The smallest frame enclosing a set of frames.
 *
 * This is the box drawn around a group, and the box its resize handles act on.
 * An empty set has no box at all rather than a zero-sized one at the origin —
 * a caller with nothing selected should draw nothing, not a dot in the corner.
 */
export const boundingBoxOf = (frames) => {
    const list = (frames || []).filter(Boolean);
    if (!list.length) return null;

    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    list.forEach((frame) => {
        const x = Number(frame.x) || 0;
        const y = Number(frame.y) || 0;
        x0 = Math.min(x0, x);
        y0 = Math.min(y0, y);
        x1 = Math.max(x1, x + (Number(frame.w) || 0));
        y1 = Math.max(y1, y + (Number(frame.h) || 0));
    });
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
};

/**
 * Move one edge or corner of a frame, as dragging a resize handle does.
 *
 * `handle` is a compass direction: 'n', 'se', and so on. Shared by the
 * single-object handles and the group box so the two cannot drift — a group
 * that resized by different rules than the objects inside it would be a
 * puzzle to use.
 */
export const applyResizeHandle = (startFrame, handle, dx, dy) => {
    const next = { ...startFrame };
    if (handle.includes('e')) next.w = startFrame.w + dx;
    if (handle.includes('s')) next.h = startFrame.h + dy;
    if (handle.includes('w')) { next.x = startFrame.x + dx; next.w = startFrame.w - dx; }
    if (handle.includes('n')) { next.y = startFrame.y + dy; next.h = startFrame.h - dy; }
    return next;
};

/**
 * Where one frame lands when the box around it is resized.
 *
 * Each frame keeps its position and size *relative to the box*, so a group
 * scales as one arrangement: the spacing between its objects grows and shrinks
 * with it instead of the objects sliding around inside a fixed layout.
 *
 * `scaleText` multiplies the block's text scale by the same factor, which is
 * what makes a scaled group of captions readable rather than a set of boxes
 * that changed size while their words stayed put. The smaller of the two axes
 * is used so text never outgrows the box holding it.
 */
export const scaleFrameWithinBox = (frame, startBox, nextBox, { scaleText = false } = {}) => {
    const sx = startBox.w ? nextBox.w / startBox.w : 1;
    const sy = startBox.h ? nextBox.h / startBox.h : 1;

    const next = {
        x: nextBox.x + ((Number(frame.x) || 0) - startBox.x) * sx,
        y: nextBox.y + ((Number(frame.y) || 0) - startBox.y) * sy,
        w: (Number(frame.w) || 0) * sx,
        h: (Number(frame.h) || 0) * sy
    };

    const currentScale = Number(frame.s);
    const base = Number.isFinite(currentScale) ? currentScale : 1;
    if (scaleText) {
        next.s = clamp(base * Math.min(sx, sy), MIN_TEXT_SCALE, MAX_TEXT_SCALE);
    } else if (Number.isFinite(currentScale)) {
        // Not scaling the text, but a block that already had a scale keeps it.
        next.s = currentScale;
    }
    return next;
};
